/**
 * Pure ground-elevation query over a baked world region's `.GI` data
 * (`docs/formats/gi-format.md`) — no three.js, no engine dependency, so it
 * is testable as plain arithmetic and safe for a flight-model adapter to
 * call every tick. `groundHeightAt` is the whole surface this module
 * exposes; everything else is how it gets its input.
 *
 * **What this reproduces.** Per entity, per tick, the original clears a
 * ground record, gathers nearby ground objects with a broad-phase distance
 * test, then for each candidate runs `giFacetContains` (a per-edge sign
 * test) followed by `giFacetHeightAt` (a plane evaluation) — gi-format.md
 * §8. This module does the same three steps, in the same order, over the
 * region's pre-scaled `groundObjects` map (`world-bake.ts`'s
 * `buildGroundObjects`, already in metres) instead of raw `.TBL` units:
 *
 * 1. **Broad phase**: skip a placement whose extent box can't contain the
 *    query point (a rectangular approximation of the engine's octagonal
 *    test — a safe superset, never a false negative).
 * 2. **Containment**: `boundingBoxOnly` objects skip straight to "yes" (the
 *    engine's own shortcut, gi-format.md §3); everything else walks its
 *    facet's corners with the same `n · (P − V) ≤ 0` sign test
 *    `giFacetContains` runs (gi-format.md §5) — `edgeNormalX/Y` are raw
 *    integers, but the sign of a dot product survives a uniform positive
 *    rescale of the other operand, so testing in metres against a raw-unit
 *    normal is exact, not an approximation (see `world-bake.ts`'s
 *    `WorldGroundVertex` doc comment for the algebra).
 * 3. **Height**: the plane formula `giFacetHeightAt` evaluates, also unit-
 *    independent for the same reason (`world-bake.ts`'s `WorldGroundFacet`
 *    doc comment) — `baseHeightM` is the only term that needed the
 *    object's own scale exponent applied, and that already happened at
 *    bake time.
 *
 * **Skipped, deliberately**: the engine's "no rotation" rule (gi-format.md
 * §5 — `giTestObjectAt` never rotates a footprint) means a placement's own
 * `rotationDegrees` is irrelevant here, so `buildGroundPlacements` below
 * never reads it; and the movement-credit distance cache the original uses
 * to skip a query entirely on a cache hit (gi-format.md §8) is a
 * performance detail with no observable effect on the *answer*, so it's not
 * reproduced — a caller that wants it can memoize `groundHeightAt` itself.
 *
 * **The frame.** A query point is `(worldX, worldZ)` in the *same*
 * unrecentred, unrotated world-metre frame `placementWorldTransform`
 * produces at `origin = ORIGIN_ZERO` — i.e. `.WLD` `x`/`y` converted to
 * metres and run through the `y -> -Z` flip, nothing else. A caller
 * recentring its own coordinates around a chosen origin (`/fly`'s start
 * aerodrome, `flight/fly-region.ts`) must build `GroundPlacement`s with the
 * *same* origin it recenters everything else with — `buildGroundPlacements`
 * takes `origin` for exactly this reason, mirroring `placementWorldTransform`'s
 * own parameter.
 *
 * **One surface, two readers.** Everything above is about the *placed* objects;
 * the ground between them is `SurfaceHeightAt`, and the contract is that it is
 * the **drawn** surface — the same sampler the renderer drapes through. This
 * function and `three/world-instancer.ts` are the two readers of that one
 * answer, and when they were allowed to hold separate ones (an analytic field
 * here, the lattice mesh there) the aeroplane fell through the road. See
 * `groundHeightAt`.
 */

import { placementWorldTransform, ORIGIN_ZERO, type PlacementOrigin } from "./placement-transform.ts";
import type { WorldGroundFacetBody, WorldGroundObjectBody, WorldRegionBody } from "./region-loader.ts";

/**
 * **The** surface height at a world XZ — the one answer the renderer and the
 * sim must share.
 *
 * Structurally a bare function rather than a `ReliefField`, and that is the
 * whole point: the thing an undercarriage touches has to be the surface that is
 * *rasterised*, which is `three/relief-ground.ts`'s `ReliefGround.drapeAt` —
 * the drawn mesh's own piecewise-linear interpolation with the datum offset
 * added back, i.e. the exact sampler every plate, road, hairline and dropped
 * building is draped through (`three/terrain-drape.ts`,
 * `three/world-shared-ref.ts`'s `instanceMatrixFor`). Taking a `ReliefField`
 * here is what let this module answer from the *analytic* field instead, and
 * the two are not the same surface — see `groundHeightAt`.
 *
 * Declared here rather than imported so this module stays free of three.js.
 */
export type SurfaceHeightAt = (x: number, z: number) => number;

/** Datum everywhere — a region with no relief, the practice range, unit tests. */
export const FLAT_SURFACE: SurfaceHeightAt = () => 0;

/** One placement whose object carries a ground-collision surface, resolved to a world position (metres, `y -> -Z` already applied) and ready to query. */
export interface GroundPlacement {
  readonly worldX: number;
  readonly worldZ: number;
  readonly ground: WorldGroundObjectBody;
}

/**
 * Filters `region.placements` to the ones whose `objectId` has a ground
 * surface (`region.groundObjects`) and resolves each to a world position —
 * the one-time setup a repeated `groundHeightAt` call amortises. `origin`
 * must match whatever recentring the caller's renderer/flight-model uses
 * for the *same* region (see this module's doc comment); pass `ORIGIN_ZERO`
 * for the region's raw, unrecentred frame.
 */
export function buildGroundPlacements(region: WorldRegionBody, origin: PlacementOrigin = ORIGIN_ZERO): readonly GroundPlacement[] {
  const out: GroundPlacement[] = [];
  for (const placement of region.placements) {
    const ground = region.groundObjects[placement.objectId];
    if (!ground) continue;
    const { position } = placementWorldTransform(placement, origin);
    out.push({ worldX: position.x, worldZ: position.z, ground });
  }
  return out;
}

/** `n · (P - V) <= 0` on every corner — `giFacetContains` (gi-format.md §5), unit-independent per this module's doc comment. */
function facetContains(facet: WorldGroundFacetBody, x: number, y: number): boolean {
  for (const v of facet.vertices) {
    if (v.edgeNormalX * (x - v.xM) + v.edgeNormalY * (y - v.yM) > 0) return false;
  }
  return true;
}

/** `giFacetHeightAt` (gi-format.md §6), evaluated in metres directly — see this module's doc comment for why the ratio survives the unit change unaltered. */
function facetHeightAt(facet: WorldGroundFacetBody, x: number, y: number): number {
  const rise = facet.gradientX * (facet.refXM - x) + facet.gradientY * (facet.refYM - y);
  return facet.baseHeightM + (rise * facet.tanScale) / 65536;
}

/**
 * The ground height (metres, world up) under `(worldX, worldZ)` — the first
 * containing `.GI` facet's own height *above the surface that facet's object is
 * drawn standing on*, or that surface itself where no placed object's footprint
 * contains the point. Placements are tested in `groundPlacements`' order, which
 * is `region.placements`' own order — the original's tie-break between
 * overlapping footprints isn't documented beyond "the broad phase finds a
 * candidate and tests it" (gi-format.md §8), so this is a reasonable, simple
 * rule rather than a reproduction of an unspecified one.
 *
 * **`surfaceHeightAt` replaces the flat base plane, and it is a port
 * departure.** gi-format.md §5's "the ground itself is still flat" is a correct
 * reading of the original and stays true of the original; this port rolls that
 * plane, for the reasons `packages/engines flight/terrain-relief.ts` sets out.
 * Two properties make it safe to put under the sim rather than behind a setting:
 * it is **derived from the region**, so every peer, replay and census recomputes
 * the identical surface and there is nothing to serialise; and the ten hills are
 * **untouched**, because a `.GI` facet still wins outright and the relief field
 * is graded to the datum across each hill's own footprint, so a hill rises from
 * 0 exactly as authored.
 *
 * ## This was a user-visible fall-through bug (report, 2026-08-24)
 *
 * *"Landing on an aerodrome or road … we actually fall through the floor."*
 * Both halves below were a **drawn-versus-collided disagreement**, and the fix
 * for both is that `surfaceHeightAt` is the *drawn* surface (see the type's own
 * doc) rather than the analytic `ReliefField` this used to take:
 *
 * 1. **The fallback answered a different surface from the one drawn.** The
 *    drawn ground is a lattice mesh and everything laid on it is draped through
 *    that mesh's interpolation; this function evaluated `relief.heightAt`
 *    *analytically*. The module doc of `three/relief-ground.ts` claimed the two
 *    differ by "under 0.3 m … well inside the undercarriage's own tolerances".
 *    Measured over 20 000 points of Verdun they differ by **1.14 m mean and
 *    10.74 m worst**, with 38 % of the map more than a metre out — because the
 *    lattice is radially *graded* and reaches ~1 km cells while the relief's
 *    finest octave has a 451 m wavelength, so out there the mesh does not
 *    sample the octave at all. That is aliasing, not sagitta, and no tolerance
 *    covers it. `ground-contact.ts` takes **one** sample at the pose origin and
 *    puts the wheels at `heightM + gearHeightM`, so this error is the sink or
 *    the float, metre for metre.
 * 2. **A `.GI` facet's height was absolute, blind to the ground its own object
 *    stands on.** `world-shared-ref.ts`'s `instanceMatrixFor` drops a placed
 *    object bodily onto `drapeAt(origin)`, but the facet height baked out of
 *    `.GI` is measured from the *flat* world's datum, so the two diverged by
 *    the whole of the local relief: Verdun's `TERR5-33` church answered
 *    **+34.29 m** where the ground it is drawn on is at **−24.96 m**, 59.2 m
 *    apart. Adding the object's own drawn base is what `world/scenery-structures.ts`
 *    already did for its fire boxes, and this now matches it.
 *
 * The ten hills are unaffected by (2) in practice as well as in principle:
 * each sits in its own `AERODROME_FLAT_M`-style flat zone
 * (`HILL_FLAT_SCALE` × its footprint), so its drawn base is 0 and the sum is
 * the authored height — measured against the drawn geometry at ratio
 * 0.978–1.000 for all three landforms.
 *
 * Omitting `surfaceHeightAt` gives the flat original behaviour, which is what
 * every caller with no region (the practice range, unit tests, the raw-`.GI`
 * agreement tests) wants.
 */
export function groundHeightAt(
  groundPlacements: readonly GroundPlacement[],
  worldX: number,
  worldZ: number,
  surfaceHeightAt: SurfaceHeightAt = FLAT_SURFACE,
): number {
  for (const placement of groundPlacements) {
    const { ground } = placement;
    const localX = worldX - placement.worldX;
    // Inverse of `modelFrameToWorld`'s `y -> -Z`: local (x, y, 0) -> world (x, 0, -y), so
    // world Z = placementZ - y, i.e. y = placementZ - worldZ.
    const localY = placement.worldZ - worldZ;

    if (Math.abs(localX) > ground.extentXM || Math.abs(localY) > ground.extentYM) continue; // broad phase

    for (const facet of ground.facets) {
      if (ground.boundingBoxOnly || facetContains(facet, localX, localY)) {
        // Sampled at the placement's *origin*, not at the query point, because
        // that is the single height `instanceMatrixFor` drops the whole object
        // to — so the box the wheels stop on is the box that is drawn.
        return surfaceHeightAt(placement.worldX, placement.worldZ) + facetHeightAt(facet, localX, localY);
      }
    }
  }
  return surfaceHeightAt(worldX, worldZ);
}
